import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useDisableMfaOtp,
  useToggleEmailMfa,
  accountErrorMessage,
  type MfaMethod,
} from '@/api/account';

interface DisableMfaDialogProps {
  open: boolean;
  method: MfaMethod;
  onOpenChange: (open: boolean) => void;
}

/**
 * Disable two-factor authentication. TOTP requires the account password to
 * confirm; email MFA is toggled off directly via the legacy endpoint.
 */
export function DisableMfaDialog({ open, method, onOpenChange }: DisableMfaDialogProps) {
  const { show } = useToast();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isTotp = method === 'totp';

  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
    }
  }, [open]);

  const onDone = () => {
    show({ tone: 'success', title: 'Two-factor disabled' });
    onOpenChange(false);
  };
  const onFail = (e: unknown) =>
    setError(accountErrorMessage(e, 'Could not disable two-factor authentication.'));

  const disableOtp = useDisableMfaOtp({ onSuccess: onDone, onError: onFail });
  const disableEmail = useToggleEmailMfa({ onSuccess: onDone, onError: onFail });

  const pending = disableOtp.isPending || disableEmail.isPending;

  function handleConfirm() {
    setError(null);
    if (isTotp) {
      if (!password) {
        setError('Enter your password to confirm.');
        return;
      }
      disableOtp.mutate({ password });
    } else {
      disableEmail.mutate({ enable: false });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Disable two-factor</DialogTitle>
          <DialogDescription>
            {isTotp
              ? 'Your account will no longer require an authenticator code at sign-in.'
              : 'Your account will no longer require an email code at sign-in.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isTotp ? (
            <Field label="Password" required error={error ?? undefined}>
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  invalid={!!error}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
          ) : (
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              {error ?? 'This will turn off email-based two-factor for your account.'}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={pending || (isTotp && !password)}>
            {pending ? 'Disabling…' : 'Disable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
